import MachinesStatus from '@/components/machines/tabs/atms/tableColumns/MachinesStatus';
import TableColumnDate from '@/components/ui/table/TableColumnDate';
import TableColumnMachine from '@/components/ui/table/TableColumnMachine';
import { parsePhoneNumber } from '@/helpers/phone';
import type { AffiliateAtm, AffiliateAtmListView, AffiliateUser, Machine } from '@/types';

import { AffiliateMachinesRemoveColumn } from '@/components/affiliates/affiliate/tabs/machines/tableColumns/AffiliateMachinesRemoveColumn';
import { UsersResetPasswordColumnV2 } from '@/components/users/tableColumns/UsersResetPasswordColumnV2';
import type { Columns } from '@bitstopco/bitstop-theme';
import type { Column } from '@bitstopco/bitstop-theme/components/Table';
import { formatCurrency } from '@bitstopco/bitstop-theme/helpers';
import { DATE_FORMATS } from '../date';

export const AFFILIATE_USERS_COLUMNS: Columns<AffiliateUser> = {
  hideSort: true,
  valueSx: {
    maxWidth: 400,
  },
  items: [
    {
      value: 'id',
      label: 'ID',
    },
    {
      value: 'first_name',
      label: 'First Name',
    },
    {
      value: 'last_name',
      label: 'Last Name',
    },
    {
      label: 'Phone Number',
      value: 'phone',
      valueMapper: (phone: AffiliateUser['phone']) =>
        phone &&
        parsePhoneNumber({
          phone,
        }).parsed,
    },
    {
      value: 'email',
      label: 'Email',
    },
    {
      value: 'created_at',
      label: 'Created At',
      valueMapper: (value: AffiliateUser['created_at']) =>
        value && (
          <TableColumnDate
            value={value}
            options={{
              timeZone: 'UTC',
            }}
          />
        ),
    },
    {
      value: 'updated_at',
      label: 'Updated At',
      valueMapper: (value: AffiliateUser['updated_at']) =>
        value && (
          <TableColumnDate
            value={value}
            options={{
              timeZone: 'UTC',
            }}
          />
        ),
    },
  ],
};

const AFFILIATE_USERS_RESET_PASSWORD_COLUMN: Column<AffiliateUser> = {
  label: 'Reset Password',
  valueMapper: (_, data) => <UsersResetPasswordColumnV2 data={data} />,
  valueSx: {
    textAlign: 'center',
  },
};

export const AFFILIATE_USERS_ADMIN_COLUMNS: (
  isResetPasswordAllowed?: boolean,
) => Columns<AffiliateUser> = (isResetPasswordAllowed) => ({
  ...AFFILIATE_USERS_COLUMNS,
  items: [
    ...AFFILIATE_USERS_COLUMNS.items,
    ...(isResetPasswordAllowed ? [AFFILIATE_USERS_RESET_PASSWORD_COLUMN] : []),
  ],
});

/**
 * Affiliate Machines columns configuration: The columns configuration will be used to generate the table with the desired data
 * * You can learn more about the Table columns configuration in the Theme Live docs: https://github.com/bitstopco/bitstop-theme#live-docs
 *
 * Used by the "Add Machines" modal, which browses ALL of the operator's machines
 * (unscoped by affiliate) to pick ones to attach -- see AFFILIATE_ATMS_COLUMNS for the
 * affiliate-scoped "Machines" tab table.
 */
export const AFFILIATE_MACHINES_COLUMNS: Columns<AffiliateAtm> = {
  defaultValue: '-',
  hideSort: true,
  labelSx: {
    whiteSpace: 'nowrap',
  },
  valueSx: {
    whiteSpace: 'nowrap',
  },
  items: [
    {
      value: 'id',
      label: 'Machine',
      valueMapper: (_, { id, name }) => (
        <TableColumnMachine
          data={
            {
              id,
              name,
            } as Machine
          }
        />
      ),
    },
    {
      value: 'machine_status_name',
      label: 'Status',
      valueMapper: (value: AffiliateAtm['machine_status_name']) =>
        value && <MachinesStatus value={value} />,
    },
    {
      value: 'serial',
      label: 'Serial',
    },
    {
      value: 'created_at',
      label: 'Created At',
      valueMapper: (value: AffiliateAtm['created_at']) =>
        value && <TableColumnDate value={value} format={DATE_FORMATS.fullVariantWithoutTimezone} />,
    },
    {
      label: 'Remove',
      valueMapper: (_, data) => <AffiliateMachinesRemoveColumn value={data} />,
    },
  ],
};

/**
 * Affiliate "Machines" tab columns configuration -- the affiliate-scoped atms list
 * (AffiliateAtmListView), including live cash. See AFFILIATE_MACHINES_COLUMNS for the
 * unscoped "Add Machines" modal table.
 */
export const AFFILIATE_ATMS_COLUMNS: Columns<AffiliateAtmListView> = {
  defaultValue: '-',
  labelSx: {
    whiteSpace: 'nowrap',
  },
  valueSx: {
    whiteSpace: 'nowrap',
  },
  items: [
    {
      value: 'id',
      label: 'Machine',
      valueMapper: (_, { id, name }) => (
        <TableColumnMachine
          data={
            {
              id,
              name,
            } as Machine
          }
        />
      ),
    },
    {
      value: 'machine_status',
      label: 'Status',
      hideSort: true,
      valueMapper: (value: AffiliateAtmListView['machine_status']) =>
        value && <MachinesStatus value={value} />,
    },
    {
      value: 'serial',
      label: 'Serial',
    },
    {
      value: 'last_sale_created_at',
      label: 'Last Sale',
      valueMapper: (value: AffiliateAtmListView['last_sale_created_at']) =>
        value && <TableColumnDate value={value} format={DATE_FORMATS.fullVariantWithoutTimezone} />,
    },
    {
      value: 'live_cash',
      label: 'Live Cash',
      valueMapper: (value: AffiliateAtmListView['live_cash']) =>
        value != null ? formatCurrency(value) : undefined,
    },
    {
      value: 'live_bill_total',
      label: 'Live Bill Total',
    },
    {
      label: 'Remove',
      hideSort: true,
      valueMapper: (_, data) => <AffiliateMachinesRemoveColumn value={data} />,
    },
  ],
};
